import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArchiveRestore, ArrowDown, Loader2, MessageSquare, MoreVertical, Search, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import Conversation from "@/model/interface/Conversation";
import Contact from "@/model/interface/Contact";
import CommunicationItem, { CommunicationItemFilter, CommunicationItemKind } from "@/model/interface/CommunicationItem";
import { useMessageDialog } from "@/context/MessageDialogContext";
import {
  useDeleteConversationPermanentlyMutation,
  useGetCommunicationItemsInfiniteQuery,
  useGetConversationQuery,
  useMarkCommunicationItemsSeenMutation,
  useRestoreConversationMutation,
  useRestoreThreadMutation,
} from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";
import { useSeenOnScreen } from "@/hooks/useSeenOnScreen";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CallActivityItem from "@/components/pages/contacts/CallActivityItem";
import CallDetail from "@/components/calls/CallDetail";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatDate } from "@/util/call";
import MessageBubble from "./MessageBubble";
import MessageComposer from "./MessageComposer";

type ThreadTab = "all" | "texts" | "calls" | "notes";

interface MessageThreadProps {
  conversation?: Conversation;
  contact?: Contact;
  showFromBar?: boolean;
  composerRef?: React.RefObject<HTMLTextAreaElement | null>;
  onRestored?: () => void;
}

const TAB_KINDS: Record<ThreadTab, CommunicationItemKind[] | undefined> = {
  all: undefined,
  texts: ["TEXT"],
  calls: ["CALL", "VOICEMAIL"],
  notes: undefined,
};

const EMPTY_COPY: Record<ThreadTab, string> = {
  all: "No messages yet. Start the conversation below.",
  texts: "No texts yet. Start the conversation below.",
  calls: "No calls with this contact yet.",
  notes: "No notes on this thread yet.",
};

const LOAD_MORE_THRESHOLD_PX = 120;
const NEAR_BOTTOM_THRESHOLD_PX = 80;
const SEARCH_DEBOUNCE_MS = 300;
const HIGHLIGHT_DURATION_MS = 3000;

const MessageThread: React.FC<MessageThreadProps> = ({
  conversation,
  contact: threadContact,
  showFromBar = true,
  composerRef,
  onRestored,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const anchor = searchParams.get("item");
  const [tab, setTab] = useState<ThreadTab>("all");
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const [highlightedAnchor, setHighlightedAnchor] = useState<string | null>(null);
  const [markSeen] = useMarkCommunicationItemsSeenMutation();
  const [restoreConversation, { isLoading: isRestoringConversation }] = useRestoreConversationMutation();
  const [restoreThread, { isLoading: isRestoringThread }] = useRestoreThreadMutation();
  const isRestoring = isRestoringConversation || isRestoringThread;
  const [deleteConversationPermanently, { isLoading: isPurging }] = useDeleteConversationPermanentlyMutation();
  const { setPendingRecipient, openMessageDialog } = useMessageDialog();
  const [confirmPurge, setConfirmPurge] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollHeightBeforeOlderRef = useRef<number | null>(null);
  const initialScrollDoneRef = useRef(false);
  const nearBottomRef = useRef(true);
  const contact = threadContact ?? conversation?.participants[0]?.contact;
  const archived = !!conversation?.deletedAt || searchParams.get("archived") === "1";
  const conversationId = conversation?.id;
  const isGroup = conversation?.isGroup ?? false;
  const filter = useMemo<CommunicationItemFilter>(() => {
    const scope = conversationId && (isGroup || !contact)
      ? { conversationId }
      : { contactId: contact?.id };
    return {
      ...scope,
      archived: archived || undefined,
      kind: TAB_KINDS[tab],
      hasNote: tab === "notes" ? true : undefined,
      q: debouncedSearch || undefined,
      anchor: anchor ?? undefined,
    };
  }, [conversationId, isGroup, contact, archived, tab, debouncedSearch, anchor]);
  const {
    data,
    isLoading,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useGetCommunicationItemsInfiniteQuery(filter, anchor ? { initialPageParam: { around: anchor } } : undefined);
  const items = useMemo(() => {
    const descending = data?.pages.flatMap((page) => page.items) ?? [];
    return [...descending].reverse();
  }, [data]);

  const threadConversationId = useMemo(() => {
    return items.find((item) => item.conversationId)?.conversationId;
  }, [items]);
  const { data: existingConversation } = useGetConversationQuery(threadConversationId ?? "", {
    skip: !!conversation || !threadConversationId,
  });
  const composerConversation = conversation ?? existingConversation;
  const unseenItemIds = useMemo(() => {
    return items.filter((item) => item.needsAttention).map((item) => item.id);
  }, [items]);
  useSeenOnScreen(scrollRef, unseenItemIds, (ids) => {
    void markSeen(ids);
  });

  const isAnchor = (item: CommunicationItem) => {
    return anchor !== null && (item.id === anchor || item.messageId === anchor || item.callId === anchor);
  };

  const threadPath = (keepArchived: boolean) => {
    const base = conversation ? `/inbox/${conversation.id}` : contact ? `/inbox/contact/${contact.id}` : "/inbox";
    return keepArchived && archived ? `${base}?archived=1` : base;
  };

  const jumpToLatest = () => {
    navigate(threadPath(true), { replace: true });
  };

  const startText = () => {
    const primaryPhone = contact?.phones?.[0]?.phoneNumberE164 || contact?.phones?.[0]?.phoneNumber;
    if (!primaryPhone) {
      return;
    }
    setPendingRecipient(primaryPhone);
    openMessageDialog();
  };

  const handleRestore = async () => {
    try {
      if (contact) {
        await restoreThread(contact.id).unwrap();
      } else if (conversation) {
        await restoreConversation(conversation.id).unwrap();
      } else {
        return;
      }
      toast.success("Conversation restored");
      onRestored?.();
      navigate(threadPath(false), { replace: true });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to restore conversation"));
    }
  };

  const handlePurge = async () => {
    if (!conversation) {
      return;
    }
    try {
      await deleteConversationPermanently(conversation.id).unwrap();
      toast.success("Conversation deleted permanently");
      setConfirmPurge(false);
      navigate("/inbox", { replace: true });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to delete conversation"));
    }
  };

  const toggleCall = (callId?: string) => {
    if (!callId) {
      return;
    }
    const expanded = expandedCallId !== callId;
    setExpandedCallId(expanded ? callId : null);
  };

  const handleScroll = () => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    nearBottomRef.current = distanceFromBottom < NEAR_BOTTOM_THRESHOLD_PX;
    if (element.scrollTop < LOAD_MORE_THRESHOLD_PX && hasNextPage && !isFetchingNextPage) {
      scrollHeightBeforeOlderRef.current = element.scrollHeight;
      void fetchNextPage();
    }
    if (distanceFromBottom < LOAD_MORE_THRESHOLD_PX && hasPreviousPage && !isFetchingPreviousPage) {
      void fetchPreviousPage();
    }
  };

  const renderItem = (item: CommunicationItem) => {
    if (item.kind === "TEXT") {
      if (item.message) {
        return (
          <div className="group">
            <MessageBubble message={item.message} />
          </div>
        );
      }
      return (
        <div className={`flex ${item.direction === "OUTBOUND" ? "justify-end" : "justify-start"}`}>
          <div className="max-w-[80%] rounded-xl bg-muted px-3 py-2 text-sm text-muted-foreground">{item.preview}</div>
        </div>
      );
    }
    const call = item.call;
    if (!call) {
      return null;
    }
    const expanded = !!call.id && expandedCallId === call.id;
    return (
      <div
        data-call-id={call.id}
        className={`overflow-hidden rounded-lg border bg-card ${expanded ? "border-primary/40" : "border-border"}`}
      >
        <CallActivityItem call={call} onOpen={() => toggleCall(call.id)} hideVoicemailPlayer={expanded} />
        {expanded && <CallDetail call={call} onRemoved={() => setExpandedCallId(null)} />}
      </div>
    );
  };

  useEffect(() => {
    initialScrollDoneRef.current = false;
    scrollHeightBeforeOlderRef.current = null;
    nearBottomRef.current = true;
    setExpandedCallId(null);
  }, [filter]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !expandedCallId) {
      return;
    }
    const card = container.querySelector<HTMLElement>(`[data-call-id="${expandedCallId}"]`);
    if (!card) {
      return;
    }
    const reveal = () => {
      card.scrollIntoView({ block: "nearest", behavior: "smooth" });
    };
    reveal();
    const observer = new ResizeObserver(reveal);
    observer.observe(card);
    return () => {
      observer.disconnect();
    };
  }, [expandedCallId]);

  useEffect(() => {
    if (!anchor || expandedCallId) {
      return;
    }
    const anchored = items.find((item) => item.call && (item.id === anchor || item.callId === anchor));
    if (anchored?.call?.id) {
      setExpandedCallId(anchored.call.id);
    }
  }, [anchor, items, expandedCallId]);

  useEffect(() => {
    setHighlightedAnchor(anchor);
    if (!anchor) {
      return;
    }
    const timer = window.setTimeout(() => setHighlightedAnchor(null), HIGHLIGHT_DURATION_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [anchor]);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element || items.length === 0) {
      return;
    }
    if (scrollHeightBeforeOlderRef.current !== null) {
      element.scrollTop += element.scrollHeight - scrollHeightBeforeOlderRef.current;
      scrollHeightBeforeOlderRef.current = null;
      return;
    }
    if (!initialScrollDoneRef.current) {
      initialScrollDoneRef.current = true;
      if (anchor) {
        const target = element.querySelector<HTMLElement>('[data-item-anchor="true"]');
        if (target) {
          target.scrollIntoView({ block: "center" });
          return;
        }
      }
      element.scrollTop = element.scrollHeight;
      return;
    }
    if (nearBottomRef.current && !hasPreviousPage) {
      element.scrollTop = element.scrollHeight;
    }
  }, [items, anchor, hasPreviousPage]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        Loading messages...
      </div>
    );
  }

  const fromLabel = conversation?.fromPhoneNumber?.alias || conversation?.fromPhoneNumber?.number;
  const rows: React.ReactNode[] = [];
  let lastDay: string | null = null;
  items.forEach((item) => {
    const day = formatDate(new Date(item.occurredAt));
    if (day !== lastDay) {
      rows.push(
        <div key={`day-${item.id}`} className="py-1 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {day}
        </div>,
      );
      lastDay = day;
    }
    const anchored = isAnchor(item);
    rows.push(
      <div
        key={item.id}
        data-item-id={item.id}
        data-item-anchor={anchored ? "true" : undefined}
        className={`rounded-lg transition-colors ${anchored && highlightedAnchor ? "bg-primary/10" : ""}`}
      >
        {renderItem(item)}
      </div>,
    );
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {showFromBar && fromLabel && (
        <div className="px-4 py-2 border-b text-xs text-muted-foreground">
          Sending from: <span className="font-medium text-foreground">{fromLabel}</span>
          {conversation?.fromPhoneNumber?.alias && (
            <span className="ml-1">({conversation.fromPhoneNumber.number})</span>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2">
        <Tabs value={tab} onValueChange={(value) => setTab(value as ThreadTab)}>
          <TabsList className="h-8">
            <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
            <TabsTrigger value="texts" className="text-xs">Texts</TabsTrigger>
            <TabsTrigger value="calls" className="text-xs">Calls</TabsTrigger>
            <TabsTrigger value="notes" className="text-xs">Notes</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search this thread"
            className="h-8 w-52 pl-8 text-sm"
          />
        </div>
      </div>
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div ref={scrollRef} onScroll={handleScroll} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2.5">
          {isFetchingNextPage && (
            <div className="flex justify-center py-1">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {items.length === 0 && (
            <div className="text-center text-muted-foreground text-sm py-8">
              {debouncedSearch ? "No matches in this thread." : EMPTY_COPY[tab]}
            </div>
          )}
          {rows}
          {isFetchingPreviousPage && (
            <div className="flex justify-center py-1">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
        {hasPreviousPage && (
          <Button
            size="sm"
            variant="outline"
            className="absolute bottom-3 left-1/2 -translate-x-1/2 shadow-md"
            onClick={jumpToLatest}
          >
            <ArrowDown className="mr-1 h-3.5 w-3.5" />
            Jump to latest
          </Button>
        )}
      </div>
      {archived ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3">
          <span className="text-sm text-muted-foreground">
            This conversation is archived. Restore it to reply, or wait for the next text or call, which restores it on its own.
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => void handleRestore()} disabled={isRestoring}>
              <ArchiveRestore className="mr-1.5 h-3.5 w-3.5" />
              Restore
            </Button>
            {conversation && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" title="Options">
                    <MoreVertical className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem className="text-destructive" onClick={() => setConfirmPurge(true)}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete texts permanently
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          <AlertDialog open={confirmPurge} onOpenChange={setConfirmPurge}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete texts permanently</AlertDialogTitle>
                <AlertDialogDescription>
                  Every text and attachment in this conversation is deleted for good. Calls stay in the call log, and consent and do-not-call records for the number stay. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => void handlePurge()} disabled={isPurging} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Delete permanently
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ) : !composerConversation ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3">
          <span className="text-sm text-muted-foreground">No text conversation with this contact yet.</span>
          <Button variant="outline" size="sm" onClick={startText} disabled={!contact?.phones?.length}>
            <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
            Send a text
          </Button>
        </div>
      ) : (
        <MessageComposer conversation={composerConversation} inputRef={composerRef} />
      )}
    </div>
  );
};

export default MessageThread;
